"use client";

// WO-GF-001-S23 · F125 → S25 · W17/R1a — the MOUNTAIN-SELECT popup, built to the founder's R1a reference
// (WALK3-REFERENCE-CONTRACTS ENG-SOT-031): a LEFT panel (search box · sliders + scrollable discipline
// filter chips · COLLAPSIBLE country accordions with per-mountain logos + an orange Details link) beside a
// RIGHT interactive map (custom gray mountain-badge markers per resort + teal airplane markers per airport).
//
// Data:
//   • the mountain LIST + logos come from the served `/api/marketplace/mountains` (region → country,
//     logoUrl) — logos are never string-built (F102); a mountain with no served logo shows its NAME.
//   • the map COORDS come from `/api/transport/geo`, which reads live's own `mountain.location` /
//     `journey_stop.location` via the transport adapter — REAL lat/lng only, never invented. When the
//     legacy RDS is unreachable the geo endpoint returns the stub set and the map shows just those.
//   • S31 · F185 — the map is now the KEYLESS one: MapLibre GL JS + OpenFreeMap, style URL behind ONE
//     config value (`@/lib/map-config`). No API key, no billing state, nothing here can 403. It replaces
//     PortMap, whose MarkerOverlay laid markers out in READING ORDER and ignored lat/lng — the reason
//     this panel used to render an alphabetical grid of resort names instead of a map of where they are.
//
// The list obeys the F125 STANDING RULE — active-product mountains sort FIRST (resortsActiveFirst). Picking
// a mountain routes to its hub (resortHubHref — the F89 ruling). Discipline chips have no per-mountain tag
// in the served data, so they render for design parity and are marked presentational (no fabricated filter).

// S26 · A3 — THIS IS NOW THE ONE MOUNTAIN SELECTOR, SITE-WIDE. The S25 build mounted it only in
// BookingChrome (the funnel pill), so every other entry point — the landing mode tiles, BOOK NOW,
// "Book Travel", the hub/sub-page/content change-mountain controls and the transport widget — still
// served one of the two OLD plain-list selectors. That is exactly what the founder walked into when he
// reported "the mountain-selection map is UNCHANGED on my path". `MountainSelectLayer` mounts this once
// per surface and every entry point now opens it (mount audit in the S26 close-out).
//
// DESTINATION RULE — unchanged, and per entry point (S13 · RW3-3 "intent-preserving mountain select"):
// an entry point that carries a MODE keeps its mode destination (marketplace → /deals-and-packages,
// transport → the hub, directory → /profile, all via hudNavHref); a neutral entry point (BOOK NOW, the
// change-mountain controls) routes to the mountain HUB (S15 · F89 ruling R1). Swapping the selector's
// CHROME must not move where a pick lands, so the mode travels with the open request.

import { useEffect, useMemo, useRef, useState } from "react";
// S31 · F185 — the selector's map is now the KEYLESS one (MapLibre + OpenFreeMap). PortMap is not
// imported here any more: its MarkerOverlay laid markers out in READING ORDER and ignored lat/lng,
// which is why this panel rendered an alphabetical grid of resorts instead of a map of where they are.
// FWLR-025-R2 — STATIC on purpose. The finding-8 pass code-split this map (KeylessMapLazy); on the
// installed desktop build the selector then sat on the loading placeholder past 30 s with the lazy
// chunks requested but never completing (evidence: fwlr025-staging-3c17-map-clean-diagnostic.json),
// while mobile passed. This is the one host mounted on every design route, so it keeps the static
// import; the four conditional hosts stay on the lazy boundary.
// SNOWCLUB-MOUNTAIN-PICKER-MAP-STAGING-20261025 — the map pane is now the HOMEPAGE's world map
// (HomepageSvgMap: the export's own d3 + Natural Earth runtime, zoomable and full-screenable) instead of
// KitTileMap's fixed 6x4 tile picture, which could not place anything outside North America and did not
// fill a measured pane. KitTileMap itself is untouched; it is simply no longer this selector's map.
import HomepageSvgMap from "@/components/map/HomepageSvgMap";
import { resolveHomepagePins } from "@/components/map/homepage-map-view";
// R3 — this selector's lamps and legend use the homepage map's tier palette so they match its pins;
// the bookability FACT and every other surface's colours are unchanged.
import { HOME_TIERS } from "@/components/map/homepage-pin";
// S31 · F184 — `isActiveProduct` is deliberately NOT imported here any more. Bookability is read from
// the served set (see the `bookable` state); importing the hardcoded predicate is what let a frozen
// 2026-07-24 snapshot drive a green chip. `resortsActiveFirst` still uses it for the F125 SORT, which
// only needs to be deterministic — and a new gate now asserts that constant against the served truth,
// so the two can no longer drift in silence.
import { RESORTS, resortsActiveFirst } from "./resorts";
import { mountainCodeFromPath } from "./hub-nav";
// 025 · S3 — the two panes' layout, pure and React-free so it is asserted in unit rather than eyeballed.
import { SELECT_NARROW_QUERY, kitPanelState, selectLayout, splitBookable } from "@/lib/mountain-select";
import { destinationFor, intentFingerprint, readSwitchIntent } from "./switch-intent";
import { useSelectorMarkers } from "./selector-markers";
// S35 · F226 — THE ONE CATALOGUE. The served fetches, the country derivation, the three-state
// bookability and the F206 light used to live in this file; the HUD dropdown now needs all four, and
// the founder's rail on F226 is "one source shared with the main selector, no second implementation".
// They moved to `mountain-catalogue`; this file reads them like any other consumer, which is what makes
// the sharing real rather than a promise to keep two copies in step.
import {
  bookabilityOf,
  COUNTRY_ORDER,
  groupByCountry,
  StatusLegend,
  StatusLight,
  useMountainCatalogue,
} from "./mountain-catalogue";
import type { Country } from "./mountain-catalogue";

// R3 — the selector's lamps/legend take the homepage tier palette so they match its pins (see above).
const HOME_LAMPS = { green: HOME_TIERS[0].color, yellow: HOME_TIERS[1].color } as const;

/** The mode the opening entry point carried, or null for a neutral one (→ the mountain hub). */
export type MountainSelectMode = "marketplace" | "transport" | "directory" | null;

const ORANGE = "#f15a22";
// Kit 2.01 group header: a flag chip + the plain country name (the design's `grp.flag` / `grp.country`).
const KIT_FLAG: Record<Country, string> = { Canada: "CA", USA: "US", Other: "" };
const KIT_COUNTRY_NAME: Record<Country, string> = { Canada: "Canada", USA: "USA", Other: "Other" };
const GREY = "#828282";
const RULE = "#cbe0e4";
// S35 · F206 — the two status-light colours moved to `mountain-catalogue` with the `StatusLight` that
// paints them, because F226 puts the same two tiers in the HUD dropdown and two copies of a colour pair
// is exactly how the two surfaces start disagreeing about what yellow means.
// S35 · F207 — `DISCIPLINES` DELETED with the chip row it fed. The founder asked for the
// Snowboard/Ski/Snow + filter-button row to go ("may return later"): it is one revert of this commit
// away, and leaving the constant behind would be dead JS pretending the feature still exists (R6-6
// accounting). The chips were always `data-presentational="true"` — the served data carries no
// per-mountain discipline tag, so they never filtered anything.

// ── FOUNDER-WALK-LIVE-RECONCILE-025 · S3 — ONE SELECTOR AT 1440 AND AT 390 ──────────────────────────
// The two panes were a fixed ROW: a list floored at 260px beside a map floored at min(520px, 52vw).
// At 390 the overlay leaves 358px, the floors alone want 260 + 203 = 463, and the dialog's own
// `overflow: hidden` clipped the map to a sliver — which is why the design's own header panel hid its
// map affordance on phones and why the founder saw different selectors at the two widths. The layout
// is a pure function of ONE fact (is the viewport narrow), returned as the two panes' styles from
// `@/lib/mountain-select` (React-free, so the arithmetic is unit-asserted rather than eyeballed);
// `selectLayout(false)` reproduces the S37 · card 86bb6gmyw values exactly, so 1440 is unchanged.

/** Is the viewport narrow, read from the browser's own media query while the selector is open. */
function useNarrowViewport(open: boolean): boolean {
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    if (!open || typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(SELECT_NARROW_QUERY);
    const apply = () => setNarrow(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, [open]);
  return narrow;
}

// S35 · F207 — `SlidersIcon` DELETED along with the chip row it decorated. Same reasoning as the
// `InfoDot` removal below: an orphaned glyph is how a removed control quietly grows back.
// S33 · F191 — `InfoDot` DELETED, not left orphaned. It was the orange ⓘ glyph beside the word "Details"
// on every info-only row; F191 replaced that whole affordance with a single quiet chevron, so its last
// call site is gone. Removed rather than kept "in case" — an unused orange glyph in this file is exactly
// how a loud style creeps back into a list the founder asked to quieten (dead-JS accounting, R6-6).

export default function MountainSelectModal({
  open,
  onClose,
  mode = null,
  locate = null,
  locateNonce = 0,
}: {
  open: boolean;
  onClose: () => void;
  mode?: MountainSelectMode;
  /** 025 · S2 — a mountain CODE to pan the map to once its pin exists (see MountainSelectLayer). */
  locate?: string | null;
  /** bumps per request so the same code can be located again on a later open. */
  locateNonce?: number;
}) {
  const [q, setQ] = useState("");
  // Kit 2.01 panel: map pane is a TOGGLE beside the list; below 768 the same toggle shows the map STACKED
  // under the list (founder ruling 2026-10-25). Opens with the map on; see `kitPanelState`.
  const narrow = useNarrowViewport(open);
  const [mapWanted, setMapWanted] = useState(true);
  const kit = kitPanelState(narrow, mapWanted);
  const layout = selectLayout(narrow, kit.showMap);
  // S35 · F226 — list metadata, bookability and listing counts now come from the ONE catalogue the HUD
  // dropdown also reads. Behaviour is unchanged: same two endpoints, same null-means-unknown rule.
  const { meta, bookable, listingCounts, metaLoaded, bookabilityLoaded } = useMountainCatalogue(open);
  // S26 — "Other" starts EXPANDED. Country comes from the served `region` string, which arrives on a
  // fetch, so for the first frames after opening EVERY mountain classifies as Other; with Other collapsed
  // that rendered a selector whose list was empty until the fetch landed (caught by the C1 mount gate
  // reading accordions=1 / rows=0). Expanded, the list is populated from the first frame and the rows
  // simply redistribute into Canada/USA when the regions arrive.
  // S31 · F183 — USA STARTS EXPANDED. The founder's ruling is "Canada first, USA VISIBLE"; USA
  // defaulting to collapsed meant every American mountain was one click from existing at all, which
  // reads as "we don't serve the USA" rather than "the USA is further down the list". Canada still
  // renders first, so the ordering ruling is untouched — only the hiding is.
  const [collapsed, setCollapsed] = useState<Record<Country, boolean>>({ Canada: false, USA: false, Other: false });
  // S35 · F227 — which mountain NAME the pointer is over, as a marker key. Null when the list is not
  // being hovered. This is the whole of the list's side of the highlight: the map decides what "lit"
  // looks like, so the two surfaces cannot disagree about pin styling.
  const [hoverKey, setHoverKey] = useState<string | null>(null);
  // S35 · F209 — the locate request. `nonce` so the same row can be located twice; see `KeylessMap`.
  const [focus, setFocus] = useState<{ key: string; nonce: number } | null>(null);

  // S32 · F186 — MUST SIT ABOVE THE EARLY RETURN (and, since S35 · F209, above the `markers` memo that
  // now reads it). Placing this useMemo down beside `destFor` (after `if (!open) return null`) made it a
  // CONDITIONAL hook: React threw "Rendered more hooks than during the previous render" and the whole
  // R1a selector stopped opening — including from the dropdown's "open the map" button. Hooks run
  // unconditionally; only the render below is conditional.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const switchIntent = useMemo(() => readSwitchIntent(mode), [mode, open]);


  useEffect(() => {
    if (!open) return;
    // an Escape the map already consumed (it was closing its full screen) must not also close the selector
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !e.defaultPrevented && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // country + grouping come from the ONE catalogue (S35 · F226). The ordering is still this file's
  // (`resortsActiveFirst`, the F125 rule) — `groupByCountry` never re-sorts what it is handed.
  // Like the Kit, a bookable mountain lives in the Bookable group ONLY; the country groups exclude it.
  const { pinned, groups } = useMemo(() => {
    const filtered = resortsActiveFirst().filter((r) => r.label.toLowerCase().includes(q.trim().toLowerCase()));
    const split = splitBookable(filtered, mountainCodeFromPath, bookable);
    return { pinned: split.pinned, groups: groupByCountry(split.rest, meta) };
  }, [q, meta, bookable]);

  // R4 — the geo fetch and the marker derivation moved VERBATIM to `useSelectorMarkers`
  // (./selector-markers), the ONE source this modal and the desktop panel map both read.
  const { markers } = useSelectorMarkers({ active: open, meta, listingCounts, bookable, switchIntent });

  // Every served mountain with a real coordinate is a pin, anywhere on Earth. A listed mountain the served geo
  // cannot place falls back to the HOMEPAGE roster's own lon/lat for that mountain (never an invented one).
  // Position only: status is the list's `bookabilityOf` and the href is the row's `destinationFor`, so a
  // fallback pin says and links exactly what its row does.
  const mapPins = useMemo(() => resolveHomepagePins(markers, RESORTS, mountainCodeFromPath, (code, href) => {
    const s = bookabilityOf(code, bookable);
    return { status: s === "unknown" ? undefined : s, href: destinationFor(href, switchIntent) };
  }), [markers, bookable, switchIntent]);
  const pinKeys = useMemo(() => new Set(mapPins.map((p) => p.key)), [mapPins]);

  // ── 025 · S2 — THE LOCATE REQUEST, HONOURED ONCE PER REQUEST, ONLY FOR A PIN THAT EXISTS ────────
  // The geo fetch lands after the open, so the requested pin may not exist on the first render; the
  // effect waits for the marker (`markers` is a dependency) and fires exactly once per (nonce, code),
  // through the SAME `focus` state the list's locate button sets — one pan mechanism, not two. A code
  // the geo endpoint never serves is never located; the selector simply opens. Above the early return
  // because hooks run unconditionally (S32 · F186).
  const locatedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      locatedRef.current = null;
      // 025-R1 — A NEUTRAL REOPEN MUST NOT INHERIT THE LAST LOCATE. `focus` and `hoverKey` are
      // component state and this component stays MOUNTED while closed (it returns null); only the
      // map inside it unmounts. So a close after "Show on the map → Vail" left `focus` pointing at
      // Vail, and the next open — from BOOK NOW, with no locate at all — remounted KeylessMap with
      // that stale focus, whose effect runs on mount and eased the map to Vail again. Same for the
      // list's own locate button before this packet. Cleared here, at close, so every open starts
      // from the map's at-rest view unless THIS request names a mountain.
      setFocus(null);
      setHoverKey(null);
      setMapWanted(true);
      return;
    }
    if (!locate) return;
    const key = `m:${locate}`;
    const stamp = `${locateNonce}:${key}`;
    if (locatedRef.current === stamp) return;
    if (!pinKeys.has(key)) return;
    locatedRef.current = stamp;
    setHoverKey(key);
    setFocus((f) => ({ key, nonce: (f?.nonce ?? 0) + 1 }));
  }, [open, locate, locateNonce, pinKeys]);

  if (!open) return null;

  // a country is shown expanded when: it is not user-collapsed, OR a search is active (so matches are
  // never hidden behind a collapsed header). The rows always live in the DOM when expanded — the active-
  // product mountains (Canada) default expanded, which keeps the standing-rule ordering visible.
  const isExpanded = (c: Country) => (q.trim() ? true : !collapsed[c]);

  // S26 — the pick destination: the opening entry point's MODE wins, else the hub (F89 R1).
  //
  // S32 · F186 — NOW SHARED WITH THE DROPDOWN, and that sharing is the point. The founder requires
  // dates/passengers/cart to survive a mountain switch through BOTH paths. Two implementations that
  // agree today because someone kept them in sync is the exact drift that produced three divergent
  // selectors before S26 and a hardcoded ONLINE chip before S31. `destinationFor` is the one builder;
  // the map path and the dropdown path cannot disagree because there is only one of them. It wraps the
  // same hudNavHref/hub resolution as before, so the MODE rule is unchanged — what it adds is carrying
  // the selection the visitor already made, which this function used to drop on the floor.
  const destFor = (href: string) => destinationFor(href, switchIntent);
  const mapLegend = <StatusLegend palette={HOME_LAMPS} />;

  const row = (r: { href: string; label: string }) => {
    const code = mountainCodeFromPath(r.href) ?? "";
    // S36 · F244 — the row no longer reads a logo. `meta[code].logoUrl` is still SERVED and still
    // consumed by the map bubble and the HUD dropdown; only this row-scoped binding is gone, because
    // with the <img> removed it was an unused local and eslint's no-unused-vars is an error here.
    // S31 · F184 — DERIVED, not declared. `bookable` is the served set; until it lands (or if the fetch
    // failed) it is null and we assert nothing about any mountain. The old `isActiveProduct(r.href)`
    // read a hardcoded Set and would happily keep claiming ONLINE for a mountain whose product was
    // pulled months ago.
    const status = bookabilityOf(code, bookable);
    const known = status !== "unknown";
    const isBookable = status === "bookable";
    return (
      <div key={r.href} data-mountain-row={r.label.trim()}
        data-active-product={isBookable ? "true" : "false"}
        data-bookability={known ? (isBookable ? "bookable" : "info-only") : "unknown"}
        // S35 · F227 — hovering the NAME lights the PIN. Deliberately NOT a pan: the founder's ruling is
        // that hover-pan is jumpy, and panning is the locate button's job (F209).
        data-mountain-marker-key={`m:${code}`}
        onMouseEnter={() => setHoverKey(`m:${code}`)}
        onMouseLeave={() => setHoverKey((h) => (h === `m:${code}` ? null : h))}
        // keyboard parity: a customer tabbing the list gets the same map feedback a mouse gets, which is
        // the S29 lesson (a state a pointer can reach and a keyboard cannot is a half-built control).
        onFocus={() => setHoverKey(`m:${code}`)}
        onBlur={() => setHoverKey((h) => (h === `m:${code}` ? null : h))}
        // S35 · F205 — LARGER ROWS. 8px→14px vertical padding; the row is a touch target and a scan
        // target, and at 34px tall thirty of them read as a spreadsheet.
        // S36 · F247 — SHORTER ROWS, and the founder's own reason is the mechanism: "with the logo gone
        // the row no longer needs that height". The 56×40 logo box was what set the row's content height,
        // so removing it (F244) is what BUYS the reduction — the padding only stops the row from keeping
        // the space its tallest child no longer occupies. 14px→8px, which lands the row just above the
        // 40px touch floor rather than at F205's spreadsheet-avoiding 55px. Measured old→new in the
        // close-out; the light, the name, the locate button and the Book action all still fit on one line.
        style={{ display: "flex", alignItems: "center", gap: 2, padding: "0 2px", borderRadius: 10,
          background: hoverKey === `m:${code}` ? "var(--sg-menu-hover, #f1f7f8)" : "transparent" }}>
        <a href={destFor(r.href)} data-mountain-entry={r.label.trim()} data-code={code}
          style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, padding: "9px 8px", textDecoration: "none", color: "#222", minWidth: 0 }}>
          {/* S35 · F206 — THE STATUS LIGHT, ski-run convention: GREEN = bookable here, YELLOW = the
              mountain is live but info-only. There is deliberately NO RED TIER (the founder's ruling):
              red on a ski run means "expert", and on a list of resorts it would read as "closed" or
              "broken", which is a claim we cannot make. A mountain whose bookability is NOT YET KNOWN
              (the served fetch has not landed, or failed) renders NO light at all rather than guessing —
              same rule the S31 F184 chip already obeys, because an unknown painted yellow is a lie. */}
          <StatusLight status={status} palette={HOME_LAMPS} />
          {/* S36 · F244 — THE LOGO IS GONE FROM THE ROW. SUPERSEDES S35 · F205's logo treatment (which
              had just grown this box 40→56 wide, 28→40 tall); the founder saw the built result and
              changed his mind, so F205 is not "wrong", it is superseded as of 2026-07-28.
              What the logo box cost, measured before removal: it was the tallest thing in the row, so it
              alone set the 55px row height that F247 is about — the two findings are one change.
              `logo` is still read from the served catalogue above and is deliberately NOT deleted from
              the data path: the map bubble and the HUD dropdown consume the same `meta[code].logoUrl`,
              and dropping the field to tidy up one row would take it from surfaces this card never
              walked. Nothing invented, nothing else re-styled. */}
          {/* S35 · F205 — "names larger, NOT bold": 14→17px, and weight 600→400. Both halves matter —
              bumping the size while leaving it semibold is what the founder was looking at. */}
          <span style={{ flex: 1, fontSize: 14, fontWeight: isBookable ? 600 : 400, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.label.trim()}</span>
          {/* S36 · F245 — THE GREEN WORD "BOOKABLE" IS GONE FROM THE ROW. SUPERSEDES the row-label half
              of S35 · F206; the STATUS LIGHT above stays and is now the sole carrier of the meaning in
              the row, read against the F251 legend (GREEN = bookable · YELLOW = information only, no
              booking). The founder's reason for the chip being redundant is exact: "The button already
              says it" — the bookable row still ends in the filled orange Book action reading "Bookable",
              so a bookable row said the same word twice, once in green text and once on the CTA.
              THE DERIVATION IS UNCHANGED — `isBookable` still comes from the served `bookable` set via
              `bookabilityOf()` (S31 · F184), and the light and the map pin still read that same call, so
              "yellow in the list, yellow on the map" holds. Only the duplicate WORD is removed.
              ⚠ The gate leg that used to prove derivation through this chip has been RE-AIMED onto the
              light, not deleted — see `bookability-is-derived-not-declared`. */}
        </a>
        {/* S31 · F184 — BOOKABLE AND INFO-ONLY ARE NO LONGER THE SAME CONTROL. Every row used to end in
            an identical orange "ⓘ Details" link, so a mountain you can book and a mountain that is a
            reference page offered the customer exactly the same affordance — the misleading state the
            finding names. A bookable row now leads with a filled BOOK action; an info-only row keeps
            Details, styled as the quiet secondary it is. Both still route through `destFor`, so RW3-3
            intent preservation is untouched. */}
        {/* S35 · F209 — THE LOCATE AFFORDANCE (founder's recommended default, taken). Hover lights the
            pin; THIS pans the map to it. It is a real <button>, not an icon with a click handler, so it
            is tabbable and announced — and it only renders for a mountain the map actually holds a
            marker for, because a control that promises to show you a place we have no coordinate for is
            the dead-control class S29 exists to prevent. */}
        {!narrow && pinKeys.has(`m:${code}`) ? (
          <button type="button" data-mountain-locate={code}
            aria-label={`Show ${r.label.trim()} on the map`} title="Show on map"
            onClick={() => { setMapWanted(true); setFocus((f) => ({ key: `m:${code}`, nonce: (f?.nonce ?? 0) + 1 })); }}
            onMouseEnter={() => setHoverKey(`m:${code}`)}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 28, height: 28,
              flex: "0 0 28px", marginRight: 4, border: "none", background: "none", borderRadius: 8, cursor: "pointer", color: GREY }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
              <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          </button>
        ) : null}
        {isBookable ? (
          <a href={destFor(r.href)} data-mountain-book
            style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "#fff", background: ORANGE,
              padding: "4px 12px", borderRadius: 999, textDecoration: "none", flex: "0 0 auto", fontWeight: 700 }}>
            Bookable
          </a>
        ) : (
          // S33 · F191 — QUIET AFFORDANCE, NO LABEL SPAM.
          // Founder, walking Phase-1B: "Details across all mountains is too messy." S31 had already made
          // this link grey rather than orange, but 28 info-only rows each still ended in the WORD
          // "Details" — the same phrase repeated down the whole list, which is the mess he is describing.
          // The row itself is already a link (the `data-mountain-entry` anchor spans logo + name and takes
          // `flex: 1`), so the text was never carrying the affordance — it was only announcing it 28 times.
          // What is left is a single subtle chevron: an affordance, not a sentence. No red, no repeated
          // label, and exactly ONE loud CTA style remains in the list (the bookable rows' orange Book).
          // `data-mountain-details` stays as the hook so the existing checks still identify these rows.
          <a href={destFor(r.href)} data-mountain-details aria-label={`${r.label.trim()} information`}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 24, flex: "0 0 24px",
              color: "#9aa4ae", textDecoration: "none" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        )}
      </div>
    );
  };

  return (
    <div data-mountain-select-overlay onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, zIndex: 2100, background: "rgba(31,79,95,.35)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: narrow ? 0 : "72px 16px 16px", fontFamily: 'var(--sg-font-primary, "Roboto", system-ui, sans-serif)' }}>
      {/* S26 — `data-behavior="nav-menu"` is the SAME contract the three old header dropdowns exposed
          (toggle → menu → `a[data-mountain-entry]`). Keeping it here means the one-routing-rule checks
          (entries-route-to-mountain-hub / switch-updates-mountain-memory / toggle-opens-and-closes) now
          assert the ONE selector instead of three divergent ones — which is the defect that let the old
          selectors survive S25 unnoticed. */}
      <div data-mountain-select data-behavior="nav-menu" data-switch-intent={intentFingerprint(switchIntent)} role="dialog" aria-label="Select your mountain"
        // S26 — an explicit READINESS marker. The country grouping depends on the served `region`, so for
        // the first frames every mountain is "Other" and the accordions have not settled. Without a signal
        // to wait on, a check either races the fetch or sleeps an arbitrary amount; this states the fact
        // instead of guessing at it.
        data-mountain-meta-loaded={metaLoaded ? "true" : "false"}
        // S35 card-1b — THE SECOND READINESS FACT, and it is not cosmetic. The served bookability is
        // what decides whether a row ends in a BOOK action or a Details chevron, so until it lands the
        // list is not merely un-styled, it is a DIFFERENT list: `interactionstates` measured the first
        // Details link at y=227, the fetch landed, that row became bookable, the first Details link
        // became a different node at y=391, and the hover it had already performed was left pointing at
        // empty space. It reported "hover does nothing" against a build whose hover was correct.
        // Publishing the fact lets a gate WAIT on it; a fixed sleep would pass here and flake elsewhere.
        data-mountain-bookability-loaded={bookabilityLoaded ? "true" : "false"}
        // 025 · S3 — which of the two layouts is live, so a probe at 390 can assert "stacked" rather
        // than infer it from a clipped map. Locate is published the same way (`data-map-located`).
        data-mountain-select-layout={layout.name}
        data-mountain-select-locate={locate ?? ""}
        style={{ width: kit.panelWidth, maxWidth: "100%", maxHeight: narrow ? "100%" : "84vh", height: narrow ? "100%" : undefined, background: "#fff", border: "1px solid var(--sg-border, #cbe0e4)", borderRadius: narrow ? 0 : 14, boxShadow: "0 30px 80px rgba(31,79,95,0.253)", overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {/* Kit 2.01 header: "Pick your mountain" label; the legend chips, search and map toggle follow in the list. */}
        <div style={{ flex: "none", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, padding: "16px 18px 4px" }}>
          <strong style={{ fontFamily: "var(--sg-font-secondary, inherit)", fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#222" }}>Pick your mountain</strong>
          <button type="button" aria-label="Close" onClick={onClose} style={{ border: "none", background: "none", fontSize: 22, lineHeight: 1, cursor: "pointer", color: GREY }}>×</button>
        </div>

        {/* two panels: LEFT list + RIGHT interactive map (R1a) */}
        <div style={{ display: "flex", flexDirection: layout.direction, gap: 0, minHeight: 0, flex: 1 }}>
          {/* LEFT */}
          {/* S37 · card 86bb6gmyw — THE MENU COLUMN IS SIZED TO ITS TEXT, AND THE MAP TAKES THE REST.
              Founder report 2026-07-31: "map should be WIDER / BIGGER; the menu column NARROWER — fit to
              the text, not a fixed wide column."
              WHAT IT WAS: `width: 400, flex: "1 1 400px"` beside a map on `1 1 360px`. Two grow-able
              panes with near-equal bases, so every pixel the dialog gained was split roughly in half and
              the list kept 400px whether its longest row needed it or not — MEASURED, the widest row's
              content was 355px at 1440, so ~45px of that column was holding nothing.
              WHAT IT IS NOW: `flex: "0 1 auto"` — the column takes its CONTENT width and does not grow,
              so the map gets all the freed space and every pixel the dialog gains. `max-content` on the
              inner list is what makes "fit to the text" real rather than approximate; `maxWidth` keeps a
              long resort name from starving the map, and `minWidth` keeps the row's own controls (light,
              name, locate, Book) on one line at 700 where the dialog itself is narrow. */}
          {/* 025 · S3 — the pane styles come from `selectLayout`; `split` reproduces the S37 values above exactly. */}
          <div data-mountain-select-list style={layout.list}>
            <div style={{ padding: "6px 18px 12px" }}>
              {/* S36 card 1 · F251 — the LIST legend (the Kit's Bookable / Information-only chips). */}
              <div data-mountain-list-legend style={{ marginBottom: 12 }}><StatusLegend palette={HOME_LAMPS} /></div>
              <div style={{ position: "relative", marginBottom: kit.toggleVisible ? 10 : 0 }}>
                <input data-mountain-search value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search resorts…" aria-label="Search resorts"
                  style={{ width: "100%", height: 38, padding: "0 34px 0 12px", borderRadius: 9, border: "1px solid var(--sg-glacier-200, #cbe0e4)", fontSize: 14, boxSizing: "border-box", outline: "none" }} />
                <span aria-hidden style={{ position: "absolute", right: 12, top: 9, color: GREY }}>⌕</span>
              </div>
              {kit.toggleVisible ? (
                <button type="button" data-mtn-open-map aria-pressed={kit.showMap} onClick={() => setMapWanted((w) => !w)}
                  style={{ width: "100%", height: 38, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: "#fff", color: "var(--sg-glacier-700, #1f4f5f)", border: "1px solid var(--sg-glacier-200, #cbe0e4)", borderRadius: 9, cursor: "pointer", fontFamily: "var(--sg-font-secondary, inherit)", fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                  {kit.toggleLabel}
                </button>
              ) : null}
              {/* S35 · F207 — THE SNOWBOARD/SKI/SNOW + FILTER ROW IS REMOVED, whole. Founder ruling on
                  the 2026-07-27 walk; "may return later", so it goes as one contiguous deletion that a
                  single revert restores rather than being commented out in place. */}
            </div>

            {/* collapsible country accordions */}
            <div style={{ overflowY: "auto", flex: 1, minHeight: 0, padding: "0 10px 14px" }}>
              {/* S31 · F183 — WHISTLER IS PINNED ABOVE THE ACCORDIONS.
                  It is the mountain the business actually runs on (63 of the 70 served listings) and it
                  was sitting inside the Canada accordion, one row among thirty, reachable only after the
                  customer had already decided to go looking. Pinned, it is the first thing in the list
                  and needs no accordion opened.
                  It is NOT removed from Canada: the accordions stay a complete geographic index, so a
                  customer scanning Canada still finds Whistler where they expect it. The pin is an
                  additional shortcut, not a relocation — and `pinnedRow` renders through the SAME `row`
                  helper, so its bookability treatment, chip and destination cannot drift from the rest. */}
              {pinned.length ? (
                <section data-mountain-pinned="whistler" style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: GREY, textTransform: "uppercase", letterSpacing: "0.5px", padding: "8px 0 4px" }}>
                    Bookable
                  </div>
                  {pinned.map(row)}
                </section>
              ) : null}
              {COUNTRY_ORDER.map((country) => {
                const countryRows = groups[country];
                return countryRows?.length ? (
                  <section key={country} data-mountain-country={country} data-collapsed={isExpanded(country) ? "false" : "true"} style={{ marginBottom: 10 }}>
                    <button type="button" data-mountain-country-toggle={country} aria-expanded={isExpanded(country)}
                      onClick={() => setCollapsed((c) => ({ ...c, [country]: !c[country] }))}
                      style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", border: "none", background: "#fff", cursor: "pointer", padding: "10px 8px 6px", fontFamily: "var(--sg-font-secondary, inherit)", fontSize: 11, fontWeight: 700, color: GREY, textTransform: "uppercase", letterSpacing: "0.14em", position: "sticky", top: 0, zIndex: 3 }}>
                      {KIT_FLAG[country] ? (
                        <span aria-hidden style={{ display: "inline-grid", placeItems: "center", minWidth: 22, height: 16, padding: "0 5px", background: "var(--sg-glacier-50, #eef4f5)", border: "1px solid var(--sg-glacier-100, #cbe0e4)", borderRadius: 4, fontSize: 9, letterSpacing: "0.06em", color: "var(--sg-glacier-700, #1f4f5f)" }}>{KIT_FLAG[country]}</span>
                      ) : null}
                      <span>{KIT_COUNTRY_NAME[country]} <span style={{ fontWeight: 500 }}>({countryRows.length})</span></span>
                      <span aria-hidden style={{ flex: 1, height: 1, background: "var(--sg-grey-200, #e3e7ea)" }} />
                      <span aria-hidden style={{ fontSize: 14, lineHeight: 1, transform: isExpanded(country) ? "rotate(90deg)" : "none", transition: "transform .15s" }}>›</span>
                    </button>
                    {isExpanded(country) ? <div>{countryRows.map(row)}</div> : null}
                  </section>
                ) : null;
              })}
              {RESORTS.filter((r) => r.label.toLowerCase().includes(q.trim().toLowerCase())).length === 0 ? (
                <p style={{ color: GREY, fontSize: 13, textAlign: "center", padding: "20px 0" }}>No resorts match “{q}”.</p>
              ) : null}
            </div>
          </div>

          {/* RIGHT — the interactive map (real coords; tiles gated on localhost = accepted overlay) */}
          {/* S36 card 1 · F251 — THE MAP LEGEND. It sits under the map rather than floating on it: the
              pins now carry the status colour, and a legend overlaying the very pins it explains would
              cover the north-west cluster, which is where the only green one is. Same `StatusLegend`
              the list and the HUD dropdown render, so the three cannot drift. */}
          {/* S37 · card 86bb6gmyw — THE MAP IS THE PANE THAT GROWS. `flex: 1` with a real basis rather
              than the old `1 1 360px` that had to share every gained pixel with the list. The dialog is
              `width: fit-content`, so the map only gets bigger if something also asks the dialog to BE
              bigger — hence the `minWidth` floor here, which is what actually widens it. */}
          {/* The floor is VIEWPORT-RELATIVE, not a constant. A flat 520px floor beside the list's 260px
              floor needs 780px of dialog, which does not exist at the founder's 700px width (the overlay
              keeps 16px of padding a side, leaving 668) — the panes would have overflowed the dialog at
              the narrowest of the three widths this card is asserted at. `min()` keeps the wide-screen
              intent and lets the map yield first where there is genuinely nothing to yield from. */}
          {/* The map fills this measured slot (`position: relative`); its full-screen mode re-styles the SAME
              mounted element as a fixed viewport layer, so the slot keeps its size and the list never moves.
              The one legend element is shown under the pane and, while full screen, inside the map. */}
          {kit.showMap ? (
          <div data-mountain-select-map style={layout.map}>
            <div data-mountain-map-slot style={{ flex: 1, minHeight: 0, position: "relative" }}>
              <HomepageSvgMap pins={mapPins} highlightKey={hoverKey} focus={focus} onHover={setHoverKey} fullscreenFooter={mapLegend} />
            </div>
            <div data-mountain-map-legend style={{ flex: "0 0 auto", padding: "8px 12px", borderTop: `1px solid ${RULE}`, background: "#fff" }}>
              {mapLegend}
            </div>
          </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
